import {
  Award,
  BadgePercent,
  BarChart3,
  Barcode,
  Box,
  Building2,
  ClipboardCheck,
  ClipboardList,
  DollarSign,
  FileCheck,
  Gift,
  FolderTree,
  FileSpreadsheet,
  Layers,
  Map,
  MapPin,
  Medal,
  Megaphone,
  MessageSquare,
  Package,
  PackageCheck,
  Receipt,
  ScrollText,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Store,
  Tag,
  Boxes,
  ScanBarcode,
  TrendingUp,
  Truck,
  Users,
  UsersRound,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'

import { LOYALTY_VIEW_SECTIONS } from '@/constants/sections'
import { hasKeycloakClaims } from '@/lib/auth/permissions'
import { INCOMING_TRANSFERS_PATH, RECEIVED_TRANSFERS_PATH } from '@/lib/routes/inventory-transfers'
import { MISSING_SCANS_PATH } from '@/lib/routes/missing-scans'
import { TRANSFER_AUDITS_PATH } from '@/lib/routes/transfer-audits'
import { type User, type UserSection } from '@/models/domain/user'

export interface SectionItem {
  name: string
  description?: string
  path?: string
  icon: LucideIcon
  requiredSections?: UserSection[]
  requireSysOp?: boolean
  requireSysAdmin?: boolean
  keycloakClaims?: string[]
  /**
   * When true, SysAdmin must hold the requiredSections explicitly —
   * SysAdmin does not bypass. Mirrors api-core's SectionCheckStrict.
   */
  strict?: boolean
  children?: SectionItem[]
}

export interface SectionGroup {
  key: string
  title: string
  items: SectionItem[]
}

export function isParentItem(item: SectionItem): item is SectionItem & { children: SectionItem[] } {
  return Array.isArray(item.children) && item.children.length > 0
}

export const sectionGroups: SectionGroup[] = [
  {
    key: 'admin',
    title: 'Admin',
    items: [
      {
        name: 'Locations',
        icon: MapPin,
        requiredSections: ['cs_locations_r'],
        children: [
          {
            name: 'Locations',
            description: 'Manage store locations',
            path: '/admin/locations',
            icon: MapPin,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'DBAs',
            description: 'Manage doing-business-as identities used by locations',
            path: '/admin/locations/dbas',
            icon: Store,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'Companies',
            description: 'Manage parent companies that own locations',
            path: '/admin/locations/companies',
            icon: Building2,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'Location Types',
            description: 'Manage location type classifications',
            path: '/admin/locations/location-types',
            icon: ShoppingBag,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'Districts',
            description: 'Manage geographic districts grouping locations',
            path: '/admin/locations/districts',
            icon: Map,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'Price Levels',
            description: 'Manage system-wide price level names',
            path: '/admin/locations/price-levels',
            icon: DollarSign,
            requiredSections: ['cs_locations_r'],
          },
          {
            name: 'Additional Taxes',
            description: 'Manage extra taxes that locations and DBAs may apply',
            path: '/admin/locations/additional-taxes',
            icon: Receipt,
            requiredSections: ['cs_locations_r'],
          },
        ],
      },
      {
        name: 'Products',
        icon: Package,
        requiredSections: ['cs_products_r'],
        children: [
          {
            name: 'Products',
            description: 'Manage the product catalog',
            path: '/admin/products',
            icon: Box,
            requiredSections: ['cs_products_r'],
          },
          {
            name: 'Product Categories',
            description: 'Manage the category hierarchy products are organized into',
            path: '/admin/products/categories',
            icon: FolderTree,
            requiredSections: ['cs_products_r'],
          },
          {
            name: 'SKUs',
            description: 'Manage barcodes that resolve to product units',
            path: '/admin/products/skus',
            icon: Barcode,
            requiredSections: ['cs_products_r'],
          },
          {
            name: 'Product Types',
            description: 'Manage product type classifications',
            path: '/admin/products/product-types',
            icon: Layers,
            requiredSections: ['cs_products_r'],
          },
        ],
      },
      {
        name: 'Inventory',
        icon: ClipboardCheck,
        requiredSections: ['cs_inventory_r'],
        children: [
          {
            name: 'Location Audit',
            description: 'View locations with open audits and finalize',
            path: '/admin/inventory/audits',
            icon: ClipboardCheck,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Inventory for Location',
            description: 'Stock on hand by product, with adjustments and zero-out',
            path: '/admin/inventory/location',
            icon: Warehouse,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Incoming Transfers',
            description: 'Transfers on their way to a location: view, receive, or re-route',
            path: INCOMING_TRANSFERS_PATH,
            icon: Truck,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Received Transfers',
            description: "Transfers a location has received, with each one's products",
            path: RECEIVED_TRANSFERS_PATH,
            icon: PackageCheck,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Transfer Audits',
            description:
              'The counts stores recorded when they accepted transfers, with each variance',
            path: TRANSFER_AUDITS_PATH,
            icon: ClipboardList,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Find Repacks / Retail Boxes',
            description:
              'Search repacks and retail boxes by date window, type, id, product or location',
            path: '/admin/inventory/collections',
            icon: Boxes,
            requiredSections: ['cs_inventory_r'],
          },
          {
            name: 'Missing Scans',
            description: 'Barcodes registers scanned and could not find, by how often and where',
            path: MISSING_SCANS_PATH,
            icon: ScanBarcode,
            requiredSections: ['cs_inventory_r'],
          },
        ],
      },
      {
        name: 'Messages',
        description: 'Create and manage configuration messages',
        path: '/admin/messages',
        icon: MessageSquare,
        requiredSections: ['cs_messages_r'],
      },
      {
        name: 'Discounts',
        description: 'Manage product discounts and promotions',
        path: '/admin/discounts',
        icon: Tag,
        requiredSections: ['cs_discounts_r'],
      },
      {
        name: 'Promotions',
        description: 'Manage promotional campaigns and offers',
        path: '/admin/promotions',
        icon: Megaphone,
        requiredSections: ['cs_promotions_r'],
      },
      {
        name: 'Loyalty Program',
        icon: Award,
        requiredSections: LOYALTY_VIEW_SECTIONS,
        children: [
          {
            name: 'Loyalty Customers',
            description: 'Search and manage loyalty program customers',
            path: '/admin/loyalty',
            icon: Award,
            requiredSections: LOYALTY_VIEW_SECTIONS,
          },
          {
            name: 'Incentive Tiers',
            description: 'Spend thresholds and point earning ratios',
            path: '/admin/loyalty/incentive-tiers',
            icon: TrendingUp,
            requiredSections: ['cs_loyalty_r'],
          },
          {
            name: 'Loyalty Ranks',
            description: 'Rank thresholds and point earning and spending rules',
            path: '/admin/loyalty/ranks',
            icon: Medal,
            requiredSections: ['cs_loyalty_r'],
          },
          {
            name: 'Loyalty Settings',
            description: 'Rewards, fraud check and bulk maintenance for the loyalty program',
            path: '/admin/loyalty/settings',
            icon: Settings,
            // api-core gates the settings groups on cs_loyalty_r (api-core#289);
            // the bulk override-lock action on the page stays SysAdmin-only.
            requiredSections: ['cs_loyalty_r'],
          },
        ],
      },
      {
        name: 'POS Users',
        description: 'Manage POS terminal users and access levels',
        path: '/admin/pos-users',
        icon: Users,
        // Matches api-core's SectionCheckStrict on the realm-wide POS user
        // types catalog: SysAdmin must hold cs_pos_users_r explicitly,
        // otherwise the page would 403 on the types fetch.
        requiredSections: ['cs_pos_users_r'],
        strict: true,
      },
    ],
  },
  {
    key: 'reports',
    title: 'Reports',
    items: [
      {
        name: 'Combined Sales',
        description: 'Payment totals, summaries, and comparisons across locations',
        path: '/reports/sales/combined',
        icon: DollarSign,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Item Sales',
        description: 'Product quantities sold and totals by category and location',
        path: '/reports/sales/items',
        icon: ShoppingCart,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Sales Summary',
        description: 'Per-location sales and returns with top products and categories',
        path: '/reports/sales/summary',
        icon: FileSpreadsheet,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Sales Comparison',
        description: 'Compare gross and net sales across locations for a date range',
        path: '/reports/sales/comparison',
        icon: BarChart3,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Pre-Settlement',
        description: 'Payment summaries by day for a location zero day',
        path: '/reports/sales/pre-settlement',
        icon: Receipt,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Settlement',
        description: 'Sales and payment summary with print support',
        path: '/reports/sales/settlement',
        icon: FileCheck,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Gift Card Redemption',
        description: 'Gift cards issued versus redeemed, system-wide and by location',
        path: '/reports/sales/gift-card',
        icon: Gift,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Discount Report',
        description: 'Discounts applied by location, by code and on product lines',
        path: '/reports/sales/discounts',
        icon: BadgePercent,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Inventory Transfer Lookup',
        description: 'Search and view inventory transfer records',
        path: '/reports/inventory/transfer-lookup',
        icon: Package,
        requiredSections: ['cs_reports_inventory_r'],
      },
      {
        name: 'Transaction Log',
        description: 'Payments and credits from the payment ledger, by location',
        path: '/reports/sales/transaction-log',
        icon: ScrollText,
        requiredSections: ['cs_reports_sales_r'],
      },
      {
        name: 'Inventory Log',
        description: 'View inventory transaction history and audit logs',
        path: '/reports/inventory/log',
        icon: ClipboardList,
        requiredSections: ['cs_reports_inventory_r'],
      },
    ],
  },
  {
    key: 'operations',
    title: 'Operations',
    items: [
      {
        name: 'Users',
        description: 'Manage system users and Keycloak accounts',
        path: '/operations/users',
        icon: UsersRound,
        requireSysOp: true,
      },
    ],
  },
]

type HasAccessFn = (sections: UserSection[], options?: { strict?: boolean }) => boolean

function passesOwnGate(item: SectionItem, user: User, hasAccess: HasAccessFn): boolean {
  if (user.sys_op) return true
  if (item.requireSysOp) return false
  if (item.requireSysAdmin && !user.sys_admin) return false
  if (item.keycloakClaims && !hasKeycloakClaims(user, item.keycloakClaims)) return false
  if (item.requiredSections) return hasAccess(item.requiredSections, { strict: item.strict })
  return true
}

export function filterSectionItems(
  items: SectionItem[],
  user: User,
  hasAccess: HasAccessFn
): SectionItem[] {
  const result: SectionItem[] = []
  for (const item of items) {
    if (!passesOwnGate(item, user, hasAccess)) continue
    if (isParentItem(item)) {
      const filteredChildren = filterSectionItems(item.children, user, hasAccess)
      if (filteredChildren.length === 0) continue
      result.push({ ...item, children: filteredChildren })
      continue
    }
    result.push(item)
  }
  return result
}

export function filterSectionGroups(user: User, hasAccess: HasAccessFn): SectionGroup[] {
  return sectionGroups
    .map((group) => ({
      ...group,
      items: filterSectionItems(group.items, user, hasAccess),
    }))
    .filter((group) => group.items.length > 0)
}

export function flattenLeaves(items: SectionItem[]): (SectionItem & { path: string })[] {
  const leaves: (SectionItem & { path: string })[] = []
  for (const item of items) {
    if (isParentItem(item)) {
      leaves.push(...flattenLeaves(item.children))
      continue
    }
    if (item.path) leaves.push(item as SectionItem & { path: string })
  }
  return leaves
}
